import { Amplify, Auth } from 'aws-amplify'
import {
  COGNITO_KEY_PREFIX,
  acceptedClientIds,
  activeCognitoPool,
  dropClientKeys,
  homeCognitoPool,
  localStorageStore,
  resolveCognitoPool,
  samePool,
  setActiveCognitoPool,
  settleCognitoPool,
  staffCognitoPool,
  staffPoolConfigError,
  type CognitoPool,
} from '@/utils/cognitoPool'
import { createCognitoStorage } from '@/utils/sessionHandoff'

/**
 * Boot config with the pool settled from this origin's localStorage. Auth.configure({ storage }) goes after
 * Amplify.configure: it merges over that config and rebuilds the user pool with this storage (Auth.js:90).
 */
export function configureAmplifyAtBoot(): CognitoPool {
  const configError = staffPoolConfigError()
  if (configError !== null) console.error(configError)

  const pool = settleCognitoPool(localStorageStore())
  Amplify.configure({
    aws_project_region: import.meta.env.VITE_AWS_REGION,
    aws_cognito_region: pool.region,
    aws_user_pools_id: pool.userPoolId,
    aws_user_pools_web_client_id: pool.clientId,
  })
  Auth.configure({ storage: createCognitoStorage() })
  setActiveCognitoPool(pool)
  if (staffCognitoPool() !== null) window.addEventListener('storage', onOtherTabSession)
  return pool
}

/** Another tab on this origin signed in, redeemed or signed out: follow it without dropping its keys. */
function onOtherTabSession(event: StorageEvent): void {
  if (event.key !== null && !(event.key.startsWith(COGNITO_KEY_PREFIX) && event.key.endsWith('.LastAuthUser'))) return
  applyCognitoPool(resolveCognitoPool(localStorageStore()))
}

/** Points Amplify at `pool`. Auth.configure merges, so the storage set in main.ts stays. */
export function applyCognitoPool(pool: CognitoPool): void {
  if (samePool(pool, activeCognitoPool())) return
  Auth.configure({ region: pool.region, userPoolId: pool.userPoolId, userPoolWebClientId: pool.clientId })
  setActiveCognitoPool(pool)
}

/** Staff pool while this origin holds a staff session and no home session, home pool otherwise. */
export function applyOriginCognitoPool(): void {
  applyCognitoPool(settleCognitoPool(localStorageStore()))
}

/** Login, invite and reset sign in against the home pool, even with staff keys on the origin. */
export function applyHomeCognitoPool(): void {
  applyCognitoPool(homeCognitoPool())
}

/** After a home sign-in: drops the staff session so the next boot resolves the home pool. */
export function dropStaffSession(): void {
  const staff = staffCognitoPool()
  if (staff !== null) dropClientKeys(localStorageStore(), staff.clientId)
}

/** Signs out the active pool (revokes and clears its keys), drops the other client's keys and re-resolves the pool. */
export async function signOutCognito(): Promise<void> {
  const signedOut = activeCognitoPool().clientId
  try {
    await Auth.signOut()
  } finally {
    const store = localStorageStore()
    for (const clientId of acceptedClientIds()) {
      if (clientId !== signedOut) dropClientKeys(store, clientId)
    }
    applyOriginCognitoPool()
  }
}
